import { useQuery } from '@apollo/client';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useAtom, useSetAtom } from 'jotai';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Responsive, WidthProvider } from 'react-grid-layout';
import type { ResponsiveProps, WidthProviderProps } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import {
  latestSnapshotIdAtom,
  portfolioIdAtom,
  portfolioNameAtom,
  portfolioCurrencyAtom,
  currentDashboardIdAtom,
} from '~/atoms/dashboardAtom';
import { globalSearchRefAtom } from '~/atoms/globalSearch';
import { toastAtom } from '~/atoms/toastAtom';
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { PageTagline } from '~/components/ui/layout/page/PageTagline';
import { AppHeader } from '~/components/ui/AppHeader';
import {
  GET_LATEST_SNAPSHOT_ID,
  GET_PORTFOLIO_NAME,
  GET_PORTFOLIO_BY_USER_ID,
} from '~/graphql/queries/portfolio';
import { isPortfolioOwner } from '~/utils/rbac';
import { useDashboard, useUpdateDashboard } from '~/hooks/useDashboard';
import { calculateNextPosition } from '~/components/ui/dashboard/utils/calculateNextPosition';
import { getWidgetDimensions } from '~/components/ui/dashboard/utils/widgetDimensions';
import { resetToDefaultLayout } from '~/components/ui/dashboard/utils/resetToDefaultLayout';
import { filterWidgetsByPattern } from '~/components/ui/dashboard/utils/filterWidgets';
import { renderWidget } from '~/components/ui/dashboard/utils/renderWidget';
import { createWidgetFromId } from '~/components/ui/dashboard/utils/createWidgetFromId';
import { PlainTableWidgetWrapper } from '~/components/ui/widgets/PlainTableWidget';
import { DEFAULT_DASHBOARD_LAYOUT_SL } from '~/constants/constants';
import '~/styles/component/bq-react-grid-layout.css';
import '~/styles/ems-blotter.css';

interface ResponsiveGridLayoutProps
  extends ResponsiveProps,
    WidthProviderProps {
  children?: React.ReactNode;
}

const ResponsiveReactGridLayout = WidthProvider(
  Responsive,
) as React.ComponentType<ResponsiveGridLayoutProps>;

const defaultLayout: Config[] = DEFAULT_DASHBOARD_LAYOUT_SL;

// Column configuration for each breakpoint
const BREAKPOINT_COLS = { lg: 12, md: 8, sm: 4, xs: 2, xxs: 1 } as const;

interface DashboardPageProps {
  dashboardId: string;
  breadcrumbLabel?: string;
  breadcrumbUrl?: string;
  pageTaglinePrefix?: string;
  /** Optional: Filter to show only specific widgets by ID pattern */
  allowedWidgets?: readonly string[];
  /** Optional: Empty state message customization */
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  emptyStateButtonLabel?: string;
  /** Optional: Initial layout configuration for reset functionality */
  initialLayout?: Config[];
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  dashboardId,
  breadcrumbLabel = 'Dashboards',
  breadcrumbUrl: _breadcrumbUrl = '/dashboard',
  pageTaglinePrefix = '',
  allowedWidgets,
  emptyStateTitle = 'No Widgets Found',
  emptyStateMessage = "This dashboard doesn't have any widgets yet. Add some widgets to get started.",
  emptyStateButtonLabel = 'Add Widget',
  initialLayout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [dashboardWidgets, setDashboardWidgets] = useState<
    React.ReactElement[]
  >([]);
  const {
    data: dashboard,
    isLoading,
    refetch: refetchDashboard,
  } = useDashboard(dashboardId);
  const updateDashboard = useUpdateDashboard(dashboardId);
  const [globalSearch] = useAtom(globalSearchRefAtom);
  const [toast] = useAtom<Toast | null>(toastAtom);
  const setLatestSnapshotId = useSetAtom(latestSnapshotIdAtom);
  const [portfolioId, setPortfolioId] = useAtom(portfolioIdAtom);
  const [_portfolioName, setPortfolioName] = useAtom(portfolioNameAtom);
  const [_portfolioCurrency, setPortfolioCurrency] = useAtom(
    portfolioCurrencyAtom,
  );
  const [_currentDashboardId, setCurrentDashboardId] = useAtom(
    currentDashboardIdAtom,
  );

  // Set the current dashboard ID in the atom for components like chat to access
  useEffect(() => {
    if (dashboardId) {
      setCurrentDashboardId(dashboardId);
    }
    return () => {
      setCurrentDashboardId(null);
    };
  }, [dashboardId, setCurrentDashboardId]);

  // Initialize portfolio ID from dashboard ONLY on initial load (when portfolioId is null) or when dashboardId changes
  // Don't override user selections from sidebar after portfolioId is set
  const dashboardIdRef = React.useRef<string | undefined>(dashboardId);

  useEffect(() => {
    const dashboardChanged = dashboardIdRef.current !== dashboardId;

    // Track dashboard changes
    if (dashboardChanged) {
      dashboardIdRef.current = dashboardId;
    }

    // Only initialize portfolioId from dashboard if:
    // 1. Dashboard has a portfolioId
    // 2. AND either:
    //    - portfolioId is currently null/undefined (initial load), OR
    //    - dashboardId changed (switching to different dashboard)
    // This prevents overriding user selections from sidebar
    if (dashboard?.portfolioId) {
      if (!portfolioId || dashboardChanged) {
        setPortfolioId(dashboard.portfolioId);
        // Clear the current snapshot ID when portfolio changes to prevent stale data
        setLatestSnapshotId(null);
      }
    }
  }, [
    dashboardId,
    dashboard?.portfolioId,
    portfolioId,
    setPortfolioId,
    setLatestSnapshotId,
  ]);

  const { data: snapshotData } = useQuery(GET_LATEST_SNAPSHOT_ID, {
    variables: { portfolioId },
    skip: !portfolioId,
    fetchPolicy: 'cache-and-network',
  });

  // Apollo automatically refetches when variables change with cache-and-network policy
  // For portfolio owners, use GET_PORTFOLIO_BY_USER_ID; otherwise use GET_PORTFOLIO_NAME
  const isPortfolioOwnerBool = isPortfolioOwner();
  const {
    data: portfolioData,
    loading: portfolioLoading,
    refetch: refetchPortfolio,
  } = useQuery(
    isPortfolioOwnerBool ? GET_PORTFOLIO_BY_USER_ID : GET_PORTFOLIO_NAME,
    {
      variables: isPortfolioOwnerBool
        ? { userId: auth.userId || '' }
        : { portfolioId },
      skip: isPortfolioOwnerBool
        ? !auth.userId || !auth.isLoaded
        : !portfolioId,
      fetchPolicy: 'cache-and-network',
    },
  );

  // When portfolioId changes, refetch data and clear snapshot to update views
  useEffect(() => {
    if (portfolioId) {
      // Clear snapshot when portfolio changes to force widgets to refetch
      setLatestSnapshotId(null);

      // Refetch portfolio data (for all users)
      if (refetchPortfolio) {
        refetchPortfolio();
      }

      // Refetch dashboard to get updated portfolio-specific data
      if (refetchDashboard) {
        refetchDashboard();
      }
    }
  }, [portfolioId, refetchPortfolio, refetchDashboard, setLatestSnapshotId]);

  // Update snapshot ID when snapshot data changes
  useEffect(() => {
    if (snapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id) {
      const snapshot = snapshotData.EQPortfolio[0].EQPortfolioSnapshots[0];
      setLatestSnapshotId(snapshot.id);
    }
  }, [snapshotData, setLatestSnapshotId]);

  // Update portfolio name and currency when portfolio data changes
  // For portfolio owners, find the portfolio that matches the selected portfolioId
  useEffect(() => {
    if (!portfolioData?.EQPortfolio) return;

    let portfolio:
      | { id: string; name?: string | null; base_currency?: string | null }
      | undefined;
    if (isPortfolioOwnerBool) {
      // For portfolio owners, find the portfolio that matches the selected portfolioId
      if (portfolioId) {
        portfolio = portfolioData.EQPortfolio.find(
          (p: { id: string }) => p.id === portfolioId,
        );
      } else {
        // If no portfolioId is set, use the first one
        portfolio = portfolioData.EQPortfolio[0];
      }
    } else {
      // For non-owners, use the first portfolio (query is filtered by portfolioId)
      portfolio = portfolioData.EQPortfolio[0];
    }

    if (portfolio) {
      if (portfolio.name) {
        setPortfolioName(portfolio.name);
      }
      if (portfolio.base_currency) {
        setPortfolioCurrency(portfolio.base_currency);
      }
    }
  }, [
    portfolioData,
    portfolioId,
    isPortfolioOwnerBool,
    setPortfolioName,
    setPortfolioCurrency,
  ]);

  const handleRemoveWidget = useCallback(
    (widgetId: string) => {
      setDashboardWidgets((prevWidgets) => {
        return prevWidgets.filter(
          (widget) => widget.props.widgetId !== widgetId,
        );
      });

      if (dashboard) {
        const currentConfig = dashboard.config || [];
        const newConfig = currentConfig.filter(
          (config) => config.id !== widgetId,
        );

        updateDashboard.mutate({
          payload: { config: newConfig },
          replace: true,
        });
      }
    },
    [dashboard, updateDashboard],
  );

  useEffect(() => {
    setDashboardWidgets([]);
  }, [dashboardId]);

  useEffect(() => {
    if (!dashboard || isLoading) return;

    if (!dashboard.config || dashboard.config.length === 0) {
      setDashboardWidgets([]);
      return;
    }

    // Filter widgets if allowedWidgets is provided
    const configsToRender = allowedWidgets
      ? filterWidgetsByPattern(dashboard.config, allowedWidgets as string[])
      : dashboard.config;

    const widgets: React.ReactElement[] = [];

    configsToRender.forEach((config) => {
      // Special handling for allocation-table widget to support batch type dropdown
      if (config.id.includes('allocation-table')) {
        // allocation-table-universe defaults to 'universe', others default to 'current_holdings'
        const defaultBatchType = config.id.includes('allocation-table-universe')
          ? 'universe'
          : 'current_holdings';
        const savedPrimaryBatchType =
          config.primaryBatchType || defaultBatchType;
        widgets.push(
          <PlainTableWidgetWrapper
            widgetId={config.id}
            key={config.id}
            primaryBatchType={savedPrimaryBatchType}
            comparisonBatchType="proposed"
            onBatchTypeChange={(batchType) => {
              const updatedConfig = dashboard.config.map((item) =>
                item.id === config.id
                  ? { ...item, primaryBatchType: batchType }
                  : item,
              );
              updateDashboard.mutate({
                payload: { config: updatedConfig },
                replace: true,
              });
            }}
          />,
        );
      } else {
        const widget = renderWidget(config);
        if (widget) {
          widgets.push(widget);
        }
      }
    });

    setDashboardWidgets(widgets);
  }, [auth.orgSlug, dashboard, isLoading, allowedWidgets]);

  const handleAddWidget = useCallback(
    (widgetId: string) => {
      // If allowedWidgets is specified, only allow those widgets
      if (
        allowedWidgets &&
        !allowedWidgets.some((pattern) => widgetId.includes(pattern))
      ) {
        console.warn(`Widget ${widgetId} is not allowed on this page`);
        toast?.show({
          severity: 'warn',
          summary: 'Widget Not Allowed',
          detail: `Widget type "${widgetId}" is not allowed on the ${pageTaglinePrefix} dashboard. Allowed widgets: ${allowedWidgets.join(', ')}`,
          life: 5000,
        });
        return;
      }

      const newWidgetId = `${widgetId}-widget-${crypto.randomUUID()}`;
      const currentConfig = dashboard?.config || [];

      // Special handling for allocation-table widget
      let widget: React.ReactElement | null;
      if (widgetId.includes('allocation-table')) {
        // allocation-table-universe defaults to 'universe', others default to 'current_holdings'
        const defaultBatchType = widgetId.includes('allocation-table-universe')
          ? 'universe'
          : 'current_holdings';
        widget = (
          <PlainTableWidgetWrapper
            widgetId={newWidgetId}
            key={newWidgetId}
            primaryBatchType={defaultBatchType}
            comparisonBatchType="proposed"
            onBatchTypeChange={(batchType) => {
              if (dashboard) {
                const updatedConfig = dashboard.config.map((item) =>
                  item.id === newWidgetId
                    ? { ...item, primaryBatchType: batchType }
                    : item,
                );
                updateDashboard.mutate({
                  payload: { config: updatedConfig },
                  replace: true,
                });
              }
            }}
          />
        );
      } else {
        // Verify widget can be created
        widget = createWidgetFromId(widgetId, newWidgetId);
      }

      if (!widget) {
        return;
      }

      // Add widget to state immediately for better UX
      setDashboardWidgets((prevWidgets) => [...prevWidgets, widget]);

      // Get dimensions for this widget type
      const { w, h } = getWidgetDimensions(widgetId);

      // Calculate the next available position
      const { x, y } = calculateNextPosition(currentConfig);

      // Use calculated positioning
      const newConfig = [
        ...currentConfig,
        {
          id: newWidgetId,
          x: x,
          y: y,
          w: w,
          h: h,
        },
      ];

      updateDashboard.mutate({
        payload: { config: newConfig },
        replace: true,
      });
    },
    [dashboard, updateDashboard, allowedWidgets],
  );

  useEffect(() => {
    if (location.state && (location.state as { widgetId?: string }).widgetId) {
      const widgetId = (location.state as { widgetId?: string }).widgetId;
      if (widgetId) {
        handleAddWidget(widgetId);
        navigate({
          to: location.pathname,
          search: location.search,
          replace: true,
          state: {},
        });
      }
    }
  }, [handleAddWidget, location, navigate]);

  // Function to reset to default layout - uses initialLayout if provided, otherwise falls back to global default
  const handleResetToDefaultLayout = useCallback(() => {
    if (dashboard) {
      const currentConfig = dashboard.config || [];
      const layoutToUse = initialLayout || defaultLayout;
      const updatedConfig = resetToDefaultLayout(currentConfig, layoutToUse);

      updateDashboard.mutate({
        payload: {
          config: updatedConfig,
        },
        replace: true,
      });
    }
  }, [dashboard, updateDashboard, initialLayout]);

  // Check if we should reset layout after PWA update for this specific dashboard
  useEffect(() => {
    const updateVersion = localStorage.getItem(
      'pwa-update-add-widgets-version',
    );
    const resetLayoutVersion = localStorage.getItem(
      'pwa-update-reset-layout-version',
    );

    if (updateVersion && dashboard) {
      // Get list of dashboards that have already been processed for this update
      const processedDashboardsStr = localStorage.getItem(
        'pwa-processed-dashboards',
      );
      const processedDashboards: string[] = processedDashboardsStr
        ? JSON.parse(processedDashboardsStr)
        : [];

      // Check if this dashboard has already been processed for this update
      if (!processedDashboards.includes(dashboardId)) {
        // Add this dashboard to the processed list
        processedDashboards.push(dashboardId);
        localStorage.setItem(
          'pwa-processed-dashboards',
          JSON.stringify(processedDashboards),
        );

        // Call reset layout after a short delay to ensure everything is loaded
        setTimeout(() => {
          handleResetToDefaultLayout();
        }, 500);
      }
    }

    // Check if we should reset layout for all views
    if (resetLayoutVersion && dashboard) {
      // Get list of dashboards that have already been processed for layout reset
      const processedLayoutDashboardsStr = localStorage.getItem(
        'pwa-processed-dashboards-layout',
      );
      const processedLayoutDashboards: string[] = processedLayoutDashboardsStr
        ? JSON.parse(processedLayoutDashboardsStr)
        : [];

      // Check if this dashboard has already been processed for layout reset
      if (!processedLayoutDashboards.includes(dashboardId)) {
        // Add this dashboard to the processed list
        processedLayoutDashboards.push(dashboardId);
        localStorage.setItem(
          'pwa-processed-dashboards-layout',
          JSON.stringify(processedLayoutDashboards),
        );

        // Call reset layout after a short delay to ensure everything is loaded
        // This will also handle migration of old allocation widgets to allocation-table-universe
        setTimeout(() => {
          handleResetToDefaultLayout();
        }, 500);
      }
    }
  }, [dashboard, dashboardId, handleResetToDefaultLayout]);

  const handleSaveLayout = useCallback(
    (layout: Layout[]) => {
      // react-grid-layout returns Layout objects that use "i" as the identifier.
      // We need to merge with existing config to preserve additional properties like primaryBatchType
      if (dashboard) {
        const transformedConfig = layout.map((layoutItem) => {
          // Find the existing config for this widget
          const existingConfig = dashboard.config.find(
            (config) => config.id === layoutItem.i,
          );

          // Merge layout updates with existing config
          return {
            ...existingConfig,
            id: layoutItem.i,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          } as Config;
        });

        updateDashboard.mutate({
          payload: {
            config: transformedConfig,
          },
          replace: true,
        });
      }
    },
    [dashboard, updateDashboard],
  );

  const handleDragStop = useCallback(
    (layout: Layout[]) => {
      handleSaveLayout(layout);
    },
    [handleSaveLayout],
  );

  // Generate responsive layouts for each breakpoint
  // Widgets expand to full width on smaller breakpoints
  const responsiveLayouts = useMemo(() => {
    const configs = dashboard?.config || [];
    const layouts: { [key: string]: Layout[] } = {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    };

    // Sort configs by y then x to maintain order
    const sortedConfigs = [...configs].sort((a, b) => {
      const yDiff = (a.y ?? 0) - (b.y ?? 0);
      if (yDiff !== 0) return yDiff;
      return (a.x ?? 0) - (b.x ?? 0);
    });

    // For md breakpoint: pack widgets into rows, 2 per row if they fit
    // Each widget gets half width (4 cols out of 8), making 2 per row
    const halfWidth = BREAKPOINT_COLS.md / 2; // 4 columns = 50%
    let mdCurrentY = 0;
    let mdCurrentX = 0;
    let mdRowMaxH = 0;

    sortedConfigs.forEach((widget) => {
      const baseLayout = {
        i: widget.id,
        x: widget.x ?? 0,
        y: widget.y ?? 0,
        w: widget.w ?? 6,
        h: widget.h ?? 9,
        minW: 2,
        minH: 4,
      };

      // lg: use original layout
      layouts.lg.push({ ...baseLayout });

      // md: pack into 2-column grid, but expand to full width if odd count in row
      const widgetH = baseLayout.h;

      if (mdCurrentX + halfWidth > BREAKPOINT_COLS.md) {
        // Move to next row
        mdCurrentY += mdRowMaxH;
        mdCurrentX = 0;
        mdRowMaxH = 0;
      }

      layouts.md.push({
        ...baseLayout,
        w: halfWidth,
        x: mdCurrentX,
        y: mdCurrentY,
      });

      mdCurrentX += halfWidth;
      mdRowMaxH = Math.max(mdRowMaxH, widgetH);

      // sm: all widgets full width
      layouts.sm.push({
        ...baseLayout,
        w: BREAKPOINT_COLS.sm,
        x: 0,
      });

      // xs: all widgets full width
      layouts.xs.push({
        ...baseLayout,
        w: BREAKPOINT_COLS.xs,
        x: 0,
      });

      // xxs: all widgets full width
      layouts.xxs.push({
        ...baseLayout,
        w: BREAKPOINT_COLS.xxs,
        x: 0,
      });
    });

    // Post-process md: expand last widget in row if it's alone
    // Group by y position
    const mdByRow = new Map<number, Layout[]>();
    layouts.md.forEach((layout) => {
      const existingRow = mdByRow.get(layout.y);
      if (existingRow) {
        existingRow.push(layout);
      } else {
        mdByRow.set(layout.y, [layout]);
      }
    });

    // Clear and rebuild md layouts with expansion for lone widgets
    layouts.md = [];
    mdByRow.forEach((rowWidgets) => {
      if (rowWidgets.length === 1) {
        // Single widget in row - expand to full width
        layouts.md.push({
          ...rowWidgets[0],
          w: BREAKPOINT_COLS.md,
          x: 0,
        });
      } else {
        // Multiple widgets - keep as is
        rowWidgets.forEach((w) => layouts.md.push(w));
      }
    });

    return layouts;
  }, [dashboard?.config]);

  // Don't render widgets if we're still loading portfolio data and have a portfolioId
  // This prevents the flash of widgets before we know if the portfolio exists
  if (portfolioId && portfolioLoading) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <div className="text-center">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <AppHeader onResetLayout={handleResetToDefaultLayout} isDashboard={true}>
        <Breadcrumb
          items={[
            {
              name:
                breadcrumbLabel ||
                dashboard?.name.replace('Dashboard', '').trim() ||
                'Dashboard',
            },
          ]}
        />
        <PageTagline
          message={`${(() => {
            if (!portfolioData?.EQPortfolio) return '';
            if (isPortfolioOwnerBool && portfolioId) {
              const portfolio = portfolioData.EQPortfolio.find(
                (p: { id: string }) => p.id === portfolioId,
              );
              return portfolio?.name || '';
            }
            return portfolioData.EQPortfolio[0]?.name || '';
          })()}`}
        />
      </AppHeader>
      <PageContent classes="px-0">
        <div style={{ paddingTop: 0 }}>
          {/* Don't show empty state while dashboard is loading (prevents flash when navigating between pages) */}
          {dashboardWidgets.length === 0 && !isLoading && dashboard ? (
            <div className="flex flex-column align-items-center justify-content-center p-8">
              <i className="pi pi-chart-bar text-6xl text-400 mb-4"></i>
              <h3 className="text-xl font-semibold text-700 mb-2">
                {emptyStateTitle}
              </h3>
              <p className="text-500 text-center mb-4">{emptyStateMessage}</p>
              <div className="flex gap-2">
                <Button
                  label={emptyStateButtonLabel}
                  icon="pi pi-plus"
                  className="p-button-primary"
                  onClick={() => {
                    if (globalSearch) {
                      globalSearch.show();
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <ResponsiveReactGridLayout
              key={dashboard?.config?.map((w) => w.id).join(',')}
              className="layout"
              layouts={responsiveLayouts}
              breakpoints={{ lg: 1236, md: 1024, sm: 768, xs: 480, xxs: 0 }}
              cols={BREAKPOINT_COLS}
              rowHeight={24}
              margin={[28, 28]}
              draggableHandle=".drag-handler"
              draggableCancel=".chat-drag-handle"
              compactType="vertical"
              onDragStop={handleDragStop}
              onResizeStop={handleDragStop}
            >
              {dashboardWidgets.map((widget) => (
                <div
                  key={widget.props.widgetId}
                  className="grid-stack-item-content overflow-auto"
                >
                  {React.cloneElement(widget, {
                    handleRemoveWidget,
                  })}
                </div>
              ))}
            </ResponsiveReactGridLayout>
          )}
        </div>
      </PageContent>
    </>
  );
};
